import React from 'react';
import { FileSpreadsheet, Users, TrendingUp, Award } from 'lucide-react';
import FileUpload from './FileUpload';

interface UploadScreenProps {
  onFileSelect: (file: File) => void;
  isLoading?: boolean;
}

const UploadScreen: React.FC<UploadScreenProps> = ({ onFileSelect, isLoading = false }) => {
  const features = [
    {
      icon: Users,
      title: 'Member Analysis',
      description: 'Analyze member contributions, attendance, and engagement patterns'
    },
    {
      icon: TrendingUp,
      title: 'Smart Scoring',
      description: 'Calculate Hot Seat scores based on contribution gaps and experience'
    },
    {
      icon: Award,
      title: 'Leaderboard',
      description: 'Rank members and identify top Hot Seat candidates'
    }
  ];

  return (
    <div className="min-h-screen bg-secondary-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <img src="/logo.png" alt="BNI Logo" className="max-w-[100px]" />
          </div>
          
          <h1 className="text-4xl font-bold text-secondary-900 mb-4">
            BNI Hot Seat Identifier
          </h1>
          <p className="text-xl text-secondary-600 max-w-3xl mx-auto">
            Identify and rank BNI members who consistently give more business than they receive, 
            maintaining strong attendance and demonstrating long-term membership experience.
          </p>
        </div>

        {/* Upload Section */}
        <div className="max-w-4xl mx-auto mb-12">
          <div className="card p-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-semibold text-secondary-900 mb-2">
                Upload Member Data
              </h2>
              <p className="text-secondary-600">
                Upload your Excel file containing member information to begin the analysis
              </p>
            </div>
            
            <FileUpload onFileSelect={onFileSelect} isLoading={isLoading} />

            {/* Download Sample File */}
            <div className="text-secondary-600 flex justify-center mt-4">
              <a
                href="/sample.xlsx"
                download
                className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md shadow hover:bg-primary-700 transition-colors duration-200 font-medium"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4" />
                </svg>
                Download Sample File
              </a>
            </div>

          </div>
        </div>

        {/* Features Section */}
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-semibold text-secondary-900 mb-2">
              How It Works
            </h3>
            <p className="text-secondary-600">
              Our algorithm analyzes multiple factors to identify the most valuable Hot Seat candidates
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="card p-6 text-center">
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-6 h-6 text-primary-600" />
                  </div>
                  <h4 className="text-lg font-semibold text-secondary-900 mb-2">
                    {feature.title}
                  </h4>
                  <p className="text-secondary-600">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Data Requirements */}
        <div className="max-w-4xl mx-auto mt-12">
          <div className="card p-8">
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <FileSpreadsheet className="w-6 h-6 text-secondary-600" />
              </div>
              <h3 className="text-xl font-semibold text-secondary-900 mb-2">
                Required Data Format
              </h3>
              <p className="text-secondary-600">
                Your Excel file should contain the following columns
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center space-x-2 text-sm">
                  <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                  <span className="font-medium">Member Name</span>
                </div>
                <div className="flex items-center space-x-2 text-sm">
                  <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                  <span className="font-medium">Years in BNI</span>
                </div>
                <div className="flex items-center space-x-2 text-sm">
                  <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                  <span className="font-medium">TYFCB Given</span>
                </div>
                
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center space-x-2 text-sm">
                  <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                  <span className="font-medium">Revenue Received</span>
                </div>                
                <div className="flex items-center space-x-2 text-sm">
                  <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                  <span className="font-medium">Green Score</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadScreen;
